// State machine + render. Single render(state) after every transition.
//
// States:
//   loading                     -> initial fetch
//   ready          {config,files=[]}  -> branding shown, file picker active
//   encrypting     {config,total}     -> JSZip + AES-GCM (no progress %)
//   uploading      {config,pct}       -> XHR upload progress
//   done           {config,blob_id}   -> success card
//   error_*        {message,...}      -> terminal error states
//
// Errors share a render path; the only thing that varies is the headline.

import {
  getConfig, getPrekey, uploadBlob, apiBase,
  LinkNotFoundError, LinkExpiredError, LinkPausedError,
  NoPrekeysError, TooLargeError, UploadFailedError,
} from './api.js';
import { verifyPrekey, encapsulate, encryptAesGcm, toBase64, fromBase64, IntegrityError } from './crypto.js';
import { buildZip, wrapWithVersion } from './envelope.js';
import {
  T, SUPPORTED,
  setLocale, detectLocale,
} from './strings.js';

const $ = (id) => document.getElementById(id);
const isMobile = () => (typeof navigator !== 'undefined') && navigator.maxTouchPoints > 0;

let STATE = { name: 'loading' };

export function boot() {
  // Apply theme + locale BEFORE first render so the SPA boots in the
  // visitor's preferred mode without a flicker.
  applyTheme();
  setLocale(detectLocale());

  // Footer year update lives here (not inline in index.html) so the
  // production CSP `script-src 'self'` doesn't block it. HTML carries a
  // static fallback for the current year if JS fails to load.
  const yearEl = document.getElementById('footer-year');
  if (yearEl) yearEl.textContent = String(new Date().getFullYear());

  renderToolbarControls();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

// --- theme + locale switchers ------------------------------------------

const THEME_KEY = 'grataq-theme';

/** Read the user's stored theme override, or null when they've never picked. */
function readThemeOverride() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch { return null; }
}

/** Set/clear the data-theme attribute based on the stored override. */
function applyTheme() {
  const override = readThemeOverride();
  if (override) {
    document.documentElement.setAttribute('data-theme', override);
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

/** Whether the page is currently rendering in dark — combines stored
 *  override + system preference for the "no override" case. Used to
 *  pick the right icon for the toggle. */
function effectiveDark() {
  const override = readThemeOverride();
  if (override) return override === 'dark';
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Flip the theme: if effectively dark, switch to light, and vice versa.
 *  Persists the choice and re-applies, then refreshes the toggle's icon
 *  in place — no state-machine re-render needed since CSS variables
 *  swap automatically. */
function toggleTheme() {
  const next = effectiveDark() ? 'light' : 'dark';
  try { localStorage.setItem(THEME_KEY, next); } catch { /* private mode */ }
  applyTheme();
  renderToolbarControls();
}

/** Mount/refresh the page-toolbar's theme toggle. The language toggle was
 *  removed: each upload link is single-language (the owner picks it at
 *  create time), so the sender doesn't switch languages on the page. */
function renderToolbarControls() {
  const themeSlot = document.getElementById('toolbar-theme');
  if (themeSlot) {
    themeSlot.innerHTML = '';
    themeSlot.appendChild(buildThemeToggle());
  }
}

// Monochrome line icons (currentColor) so the theme toggle never picks
// up an emoji font's colour — sun/moon both follow --text in either
// theme. Feather/Lucide style.
const SUN_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>';
const MOON_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';

function buildThemeToggle() {
  const dark = effectiveDark();
  const btn = el('button', 'theme-toggle');
  btn.type = 'button';
  btn.setAttribute('aria-label', dark ? T.themeToggleAriaLight : T.themeToggleAriaDark);
  btn.innerHTML = dark ? SUN_SVG : MOON_SVG;
  btn.onclick = toggleTheme;
  return btn;
}

async function start() {
  render();
  const token = readToken();
  if (!token) {
    transition({ name: 'error', message: T.errMissingToken });
    return;
  }
  try {
    const config = await getConfig(token);

    // Single-language page: the link's owner picked the language, so the
    // sender sees no toggle. Use the link's language; fall back to the
    // visitor's browser only for legacy links that have none. Re-render
    // the toolbar so the theme toggle's aria label matches the locale.
    const linkLocale = (config.language && SUPPORTED.has(config.language))
      ? config.language
      : detectLocale();
    setLocale(linkLocale);
    renderToolbarControls();

    if (config.paused) {
      transition({ name: 'error', message: T.errPaused, config });
      return;
    }
    if (config.prekeys_available === 0) {
      transition({ name: 'error', message: T.errNoPrekeys, config });
      return;
    }
    // R5: parse the sender-info schema (if any). When the link's owner
    // has configured at least one enabled field, we'll render the form
    // above the file picker; null schema / all-disabled schema = no
    // form (current upload UX unchanged).
    const schema = parseSchema(config.sender_info_schema);
    const senderInfo = hasAnyEnabledField(schema) ? seedSenderInfo(schema) : null;
    transition({ name: 'ready', token, config, files: [], schema, senderInfo, senderInfoError: null });
  } catch (err) {
    transition({ name: 'error', message: errorMessage(err) });
  }
}

/** Defensive JSON.parse for the sender_info_schema field. */
function parseSchema(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** True when the schema has at least one enabled fixed field or any custom field. */
function hasAnyEnabledField(schema) {
  if (!schema) return false;
  const f = schema.fixed || {};
  if (f.name && f.name.enabled) return true;
  if (f.surname && f.surname.enabled) return true;
  if (f.email && f.email.enabled) return true;
  if (f.phone && f.phone.enabled) return true;
  return Array.isArray(schema.custom) && schema.custom.length > 0;
}

/** Seed STATE.senderInfo from a non-empty schema: all values empty strings. */
function seedSenderInfo(schema) {
  const custom = Array.isArray(schema.custom) ? schema.custom : [];
  return {
    name: '',
    surname: '',
    email: '',
    phone: '',
    custom: custom.map((c) => ({ label: c.label || '', value: '' })),
  };
}

/** Read token from path segment first, then ?token= query, in that order. */
function readToken() {
  const path = window.location.pathname.replace(/^\//, '').replace(/\/$/, '');
  if (path) return decodeURIComponent(path);
  const q = new URLSearchParams(window.location.search).get('token');
  return q ? decodeURIComponent(q) : null;
}

function transition(next) {
  STATE = next;
  render();
}

function render() {
  const root = $('app');
  if (!root) return;
  root.innerHTML = '';
  switch (STATE.name) {
    case 'loading':    root.appendChild(viewLoading()); break;
    case 'ready':      root.appendChild(viewReady());   break;
    case 'encrypting': root.appendChild(viewProgress(T.encrypting, null)); break;
    case 'uploading':  root.appendChild(viewProgress(T.uploadingPercent(STATE.pct), STATE.pct)); break;
    case 'done':       root.appendChild(viewDone()); break;
    case 'error':      root.appendChild(viewError()); break;
  }
}

// --- views --------------------------------------------------------------

function viewLoading() {
  const wrap = el('div', 'card card--center');
  wrap.appendChild(el('div', 'spinner'));
  wrap.appendChild(el('p', 'muted', T.loading));
  return wrap;
}

function viewReady() {
  const wrap = el('div', 'card');
  wrap.appendChild(viewBranding(STATE.config));
  // Always-on encryption notice — covers BOTH the files and any form fields, and
  // shows even for file-only links (where there's no form to host it).
  wrap.appendChild(el('p', 'encryption-notice', T.encryptionNotice));
  const instructions = viewSenderInstructions(STATE.config);
  if (instructions) wrap.appendChild(instructions);

  const form = viewSenderInfoForm();
  // Form-only link (max size 0): no file picker — the form IS the upload.
  // Otherwise the normal file picker (which hosts its own submit button).
  const body = isFormOnly(STATE.config) ? viewFormOnlySubmit(!!form) : viewPicker();

  if (form && !isFormOnly(STATE.config)) {
    // A link with BOTH a sender-info form and a file picker → lay them out in two
    // columns on wide screens (the widened card has room); stacks on narrow.
    const cols = el('div', 'ready-cols');
    cols.appendChild(form);
    cols.appendChild(body);
    wrap.appendChild(cols);
  } else {
    if (form) wrap.appendChild(form);
    wrap.appendChild(body);
  }
  return wrap;
}

/** A form-only link carries no files — the owner set max upload size to 0. */
function isFormOnly(config) {
  return !!config && config.max_upload_size_bytes === 0;
}

/**
 * A normal (non form-only) link whose owner made files optional: the sender
 * may submit with just the form. `files_required` defaults to true (and is
 * absent on links created before the flag existed), so only an explicit
 * `false` opts in.
 */
function filesOptional(config) {
  return !!config && !isFormOnly(config) && config.files_required === false;
}

/**
 * Submit area for a form-only link: just the send button (the sender-info
 * form is rendered above by viewReady). Degrades to an error line if the
 * link somehow has no form fields (the creator blocks this, but be safe).
 */
function viewFormOnlySubmit(hasForm) {
  const wrap = el('section', 'picker');
  if (!hasForm) {
    wrap.appendChild(el('p', 'error-inline', T.formOnlyNoFields));
    return wrap;
  }
  wrap.appendChild(el('p', 'picker__total', T.formOnlyHint));
  const send = el('button', 'btn btn--primary btn--hero', T.formOnlySubmit);
  send.type = 'button';
  send.onclick = startUpload;
  wrap.appendChild(send);
  return wrap;
}

/**
 * R5: render the sender-info form above the file picker. Returns null
 * when no schema is set or the schema renders no fields. The GDPR
 * disclosure callout sits above the form; required fields are marked
 * with a red asterisk; an inline error band appears when validation
 * has flagged something on the last upload attempt.
 *
 * Inputs use direct DOM listeners that mutate STATE.senderInfo in
 * place — no transition() call — so typing doesn't re-render the
 * whole picker and steal focus.
 */
function viewSenderInfoForm() {
  const schema = STATE.schema;
  if (!schema || !hasAnyEnabledField(schema)) return null;

  const section = el('section', 'sender-info');

  // The encryption notice now lives once at the top of the card (viewReady),
  // covering both the form and the files — no per-form disclosure here.
  const grid = el('div', 'sender-info__grid');

  const f = schema.fixed || {};
  const addFixed = (key, label) => {
    const cfg = f[key];
    if (!cfg || !cfg.enabled) return;
    grid.appendChild(makeFieldRow({
      label,
      required: !!cfg.required,
      type: key === 'email' ? 'email' : 'text',
      value: STATE.senderInfo[key] || '',
      onInput: (v) => { STATE.senderInfo[key] = v; },
    }));
  };

  addFixed('name',    T.senderInfoFieldName);
  addFixed('surname', T.senderInfoFieldSurname);
  addFixed('email',   T.senderInfoFieldEmail);
  addFixed('phone',   T.senderInfoFieldPhone);

  const custom = Array.isArray(schema.custom) ? schema.custom : [];
  custom.forEach((c, idx) => {
    grid.appendChild(makeFieldRow({
      label: c.label || '',
      required: !!c.required,
      type: 'text',
      value: (STATE.senderInfo.custom[idx] && STATE.senderInfo.custom[idx].value) || '',
      onInput: (v) => {
        if (!STATE.senderInfo.custom[idx]) {
          STATE.senderInfo.custom[idx] = { label: c.label || '', value: v };
        } else {
          STATE.senderInfo.custom[idx].value = v;
        }
      },
    }));
  });

  section.appendChild(grid);

  if (STATE.senderInfoError) {
    const err = el('p', 'error-inline', STATE.senderInfoError);
    section.appendChild(err);
  }

  return section;
}

function makeFieldRow({ label, required, type, value, onInput }) {
  const row = el('label', 'sender-info__row');
  const labelEl = el('span', 'sender-info__label');
  labelEl.textContent = label;
  if (required) {
    const star = el('span', 'sender-info__required-mark');
    star.textContent = ' ' + T.senderInfoRequiredMark;
    labelEl.appendChild(star);
  }
  row.appendChild(labelEl);

  const input = document.createElement('input');
  input.type = type;
  input.className = 'sender-info__input';
  input.value = value;
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.addEventListener('input', (ev) => onInput(ev.target.value));
  row.appendChild(input);

  return row;
}

/**
 * Phase V: render per-link instructions from the recipient above the
 * file picker. Returns null when the link has no instructions so the
 * caller can omit the element entirely (cleaner DOM than an empty box).
 */
function viewSenderInstructions(config) {
  const text = (config.sender_instructions || '').trim();
  if (!text) return null;

  const box = el('section', 'callout');
  box.appendChild(el('p', 'callout__label', T.senderInstructionsLabel));
  // Preserve user-entered line breaks while keeping HTML escaped via
  // textContent on a <pre> styled to wrap.
  const body = el('pre', 'callout__body');
  body.textContent = text;
  box.appendChild(body);
  return box;
}

/**
 * Cascade the recipient's brand colour into the CSS accent variable so
 * the graphic accents on the page (brand-stroke under H1, link
 * underline, dropzone border, primary CTA, focus ring) shift to the
 * tenant's identity. Body text deliberately does NOT use --accent — it
 * stays in --text so a pale brand colour (yellow, neon green, etc.)
 * never makes copy unreadable.
 *
 * Hover / pressed states are derived via color-mix so any base colour
 * gets a lighter hover and a darker pressed shade without the tenant
 * having to spell each one out.
 *
 * --on-accent is picked dynamically from the accent's relative
 * luminance so primary-button text stays readable whether the tenant
 * uses purple (luminance ~0.13, light text) or yellow (luminance ~0.78,
 * dark text). Defaults from app.css are restored when primary is null.
 */
function applyTenantAccent(primary) {
  const root = document.documentElement.style;
  if (!primary) {
    root.removeProperty('--accent');
    root.removeProperty('--accent-hover');
    root.removeProperty('--accent-pressed');
    root.removeProperty('--on-accent');
    return;
  }
  root.setProperty('--accent', primary);
  root.setProperty('--accent-hover', `color-mix(in srgb, ${primary} 75%, white)`);
  root.setProperty('--accent-pressed', `color-mix(in srgb, ${primary} 80%, black)`);

  const lum = relativeLuminance(primary);
  // W3C 0.5 is the conventional cut-off for picking dark vs light
  // foreground; #2D2E33 and #F5F5FA mirror the WPF OnAccent decision.
  root.setProperty('--on-accent', lum !== null && lum > 0.5 ? '#2D2E33' : '#F5F5FA');
}

/**
 * W3C relative luminance for a `#RRGGBB` string, used to decide whether
 * white or near-black reads better on top of the accent. Returns null
 * when the input isn't a parseable hex (caller falls back to the
 * default --on-accent from CSS).
 */
function relativeLuminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function viewBranding(config) {
  applyTenantAccent(config.primary_color);

  const head = el('header', 'branding');
  head.appendChild(viewBrandingIdent(config));
  head.appendChild(viewTrustCard(config));
  return head;
}

/**
 * Left column of the header: logo, institution name (with brand-stroke
 * underline), and the optional public-info URL. Everything that
 * communicates *who* the recipient is — left-aligned on desktop, centred
 * on mobile.
 */
function viewBrandingIdent(config) {
  const ident = el('div', 'branding__ident');

  if (config.logo_url) {
    const img = el('img', 'branding__logo');
    img.src = absoluteApi(config.logo_url);
    img.alt = '';
    ident.appendChild(img);
  }

  // Brand name + URL stacked in their own column, so the logo can sit
  // beside them on desktop instead of stacking three rows tall.
  const text = el('div', 'branding__ident-text');
  text.appendChild(el('h1', 'branding__name', config.institution_name || config.handle));

  if (config.public_url) {
    const linkWrap = el('p', 'branding__link');
    linkWrap.appendChild(document.createTextNode(T.moreInfoPrefix));
    const a = el('a');
    // The stored public_url may lack a scheme (e.g. "www.banka.cz/docs").
    // Without one the browser treats href as relative to send.grataq.com
    // and the link goes nowhere — force an absolute external URL.
    a.href = externalUrl(config.public_url);
    a.textContent = config.public_url;
    a.rel = 'noopener noreferrer';
    a.target = '_blank';
    linkWrap.appendChild(a);
    text.appendChild(linkWrap);
  }
  ident.appendChild(text);

  return ident;
}

/**
 * Right column of the header: the trust signal. Verified-domain links
 * land in a prominent green card (icon + label + domain); links from a
 * personal account get the orange "Identita: Neověřena doménou"
 * variant. Sized larger than a typical badge because for the visitor
 * this is *the* "should I upload here?" cue.
 */
function viewTrustCard(config) {
  const verified = !!config.ev_domain;
  const card = el('div', 'trust-card ' + (verified ? 'trust-card--verified' : 'trust-card--unverified'));

  const header = el('div', 'trust-card__header');
  header.appendChild(el('span', 'trust-card__icon', verified ? '✓' : '⚠'));
  header.appendChild(el('span', 'trust-card__label',
    verified ? T.trustVerifiedLabel : T.trustUnverifiedLabel));
  card.appendChild(header);

  if (verified) {
    // The seal is clickable → opens the verified site (https://{ev_domain}).
    const link = el('a', 'trust-card__value trust-card__value--link', config.ev_domain);
    link.href = externalUrl(config.ev_domain);
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    card.appendChild(link);
  } else {
    card.appendChild(el('div', 'trust-card__value', T.trustUnverifiedValue));
  }

  return card;
}

/**
 * Wire drag&drop file-appending onto an element: dropped files are ADDED to the
 * current selection (not replaced), so it works both in the empty dropzone-hero
 * state and the non-empty file-list state. `activeClass` toggles a hover style.
 */
function attachDropTarget(zone, activeClass) {
  zone.addEventListener('dragover', (ev) => { ev.preventDefault(); zone.classList.add(activeClass); });
  zone.addEventListener('dragleave', () => zone.classList.remove(activeClass));
  zone.addEventListener('drop', (ev) => {
    ev.preventDefault();
    zone.classList.remove(activeClass);
    const dropped = Array.from(ev.dataTransfer?.files || []);
    if (dropped.length === 0) return;
    transition({ ...STATE, files: (STATE.files || []).concat(dropped) });
  });
}

/** Lower-case file extension (no dot), or '' if none. */
function extOf(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || '');
  return m ? m[1].toLowerCase() : '';
}

// Compact inline SVGs (24×24, currentColor) per file category. Inline DOM — not
// <img> — so they're not subject to the img-src CSP; the tenant accent flows in
// via currentColor on .picker__icon.
const FILE_SVGS = {
  doc: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M6 2h8l4 4v16H6z"/><path d="M14 2v4h4"/><path d="M9 13h6M9 17h4"/></svg>',
  sheet: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="4" y="4" width="16" height="16" rx="1"/><path d="M4 10h16M4 15h16M10 4v16"/></svg>',
  archive: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M3 7l9-4 9 4v10l-9 4-9-4z"/><path d="M3 7l9 4 9-4M12 11v10"/></svg>',
};

/** Pick a type-icon SVG by extension (images are handled separately as thumbnails). */
function svgForExt(ext) {
  if (['csv', 'tsv', 'xls', 'xlsx', 'ods', 'numbers'].includes(ext)) return FILE_SVGS.sheet;
  if (['zip', 'rar', '7z', 'gz', 'tar', 'bz2'].includes(ext)) return FILE_SVGS.archive;
  return FILE_SVGS.doc;
}

/** A thumbnail (&lt;img&gt;) for image files, or an inline type icon otherwise. */
function iconForFile(file) {
  if ((file.type || '').startsWith('image/')) {
    const img = el('img', 'picker__thumb');
    const url = URL.createObjectURL(file);
    img.src = url;
    img.alt = '';
    const free = () => URL.revokeObjectURL(url);   // free once decoded — no leak across re-renders
    img.onload = free;
    img.onerror = free;
    return img;
  }
  const icon = el('span', 'picker__icon');
  icon.innerHTML = svgForExt(extOf(file.name));
  return icon;
}

function viewPicker() {
  const wrap = el('section', 'picker');
  const files = STATE.files || [];
  const limitMB = toMB(STATE.config.max_upload_size_bytes);

  if (files.length === 0) {
    // Empty state — big drop/click hero is the page's primary CTA. On
    // touch devices the camera button rides along as a secondary option.
    wrap.appendChild(viewDropzoneHero(limitMB, !filesOptional(STATE.config)));
    if (isMobile()) {
      const row = el('div', 'picker__buttons');
      row.appendChild(viewCameraButton());
      wrap.appendChild(row);
    }
    // Files optional: let the sender submit with just the form (no files).
    // startUpload still blocks a completely empty submission.
    if (filesOptional(STATE.config)) {
      wrap.appendChild(el('p', 'picker__total', T.filesOptionalHint));
      const send = el('button', 'btn btn--primary btn--hero', T.formOnlySubmit);
      send.type = 'button';
      send.onclick = startUpload;
      wrap.appendChild(send);
    }
    return wrap;
  }

  // Non-empty state — file list, total, "+ Přidat" + (mobile) camera as
  // secondary actions, "Odeslat zašifrované" as the dominant primary CTA.
  const list = el('ul', 'picker__list');
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    const li = el('li', 'picker__item');
    li.appendChild(iconForFile(f));
    li.appendChild(el('span', 'picker__name', f.name));
    li.appendChild(el('span', 'picker__size', humanSize(f.size)));
    const rm = el('button', 'picker__remove', T.removeFile);
    rm.type = 'button';
    rm.setAttribute('aria-label', T.removeFileAria);
    rm.onclick = () => {
      const next = files.slice();
      next.splice(i, 1);
      transition({ ...STATE, files: next });
    };
    li.appendChild(rm);
    list.appendChild(li);
  }
  wrap.appendChild(list);

  const total = files.reduce((s, f) => s + f.size, 0);
  wrap.appendChild(el('p', 'picker__total', T.totalSize(toMB(total))));

  const more = el('div', 'picker__buttons');
  more.appendChild(viewAddMoreButton());
  if (isMobile()) more.appendChild(viewCameraButton());
  wrap.appendChild(more);

  // The file list itself is a drop target, so more files can be dragged in after
  // the first one (the hero dropzone is gone once files are picked).
  if (!isMobile()) wrap.appendChild(el('p', 'picker__drop-hint', T.dropMoreHint));
  attachDropTarget(wrap, 'picker--drag');

  if (total > STATE.config.max_upload_size_bytes) {
    wrap.appendChild(el('p', 'error-inline', T.errTooLarge(limitMB)));
  } else {
    const send = el('button', 'btn btn--primary btn--hero', T.uploadButton);
    send.type = 'button';
    send.onclick = startUpload;
    wrap.appendChild(send);
  }

  return wrap;
}

/**
 * Hero drop/click target shown when no file is picked yet. Visually the
 * dominant element on the page so first-time visitors immediately see
 * the only action they need to take. Drag-over toggles the active class
 * for visual feedback; drop reuses the same code path as the hidden
 * file input's change event.
 */
function viewDropzoneHero(limitMB, required) {
  const input = makeFileInput({ accept: '*/*', multiple: true });
  const zone = el('label', 'dropzone');
  zone.appendChild(input);
  zone.appendChild(el('div', 'dropzone__icon', '⬆'));
  const primary = el('p', 'dropzone__primary',
    isMobile() ? T.dropzonePrimaryMobile : T.dropzonePrimary);
  if (required) {
    // Same required convention as the form fields: a red asterisk.
    primary.appendChild(el('span', 'sender-info__required-mark', T.senderInfoRequiredMark));
  }
  zone.appendChild(primary);
  zone.appendChild(el('p', 'dropzone__hint', T.dropzoneHint(limitMB)));

  attachDropTarget(zone, 'dropzone--active');

  return zone;
}

function viewAddMoreButton() {
  const input = makeFileInput({ accept: '*/*', multiple: true });
  const label = el('label', 'btn');
  label.appendChild(input);
  label.appendChild(document.createTextNode(T.addMore));
  return label;
}

function viewCameraButton() {
  const camera = makeFileInput({ accept: 'image/*', multiple: false, capture: 'environment' });
  const label = el('label', 'btn');
  label.appendChild(camera);
  label.appendChild(document.createTextNode(T.takePhoto));
  return label;
}

function viewProgress(label, pct) {
  const wrap = el('div', 'card card--center');
  wrap.appendChild(viewBranding(STATE.config));
  wrap.appendChild(el('div', 'spinner'));
  wrap.appendChild(el('p', 'progress__label', label));
  if (pct !== null) {
    const bar = el('div', 'progress');
    const fill = el('div', 'progress__fill');
    fill.style.width = `${pct}%`;
    bar.appendChild(fill);
    wrap.appendChild(bar);
  }
  return wrap;
}

function viewDone() {
  const wrap = el('div', 'card card--center');
  wrap.appendChild(viewBranding(STATE.config));
  wrap.appendChild(el('div', 'check'));
  wrap.appendChild(el('h2', null, T.doneTitle));
  wrap.appendChild(el('p', 'muted', T.doneRef(STATE.blob_id.slice(0, 8))));
  wrap.appendChild(el('p', 'muted', T.doneRetention));
  return wrap;
}

function viewError() {
  const wrap = el('div', 'card card--center');
  if (STATE.config) {
    wrap.appendChild(viewBranding(STATE.config));
  }
  const head = el('h2', 'error', STATE.message);
  wrap.appendChild(head);
  // Only offer retry for transient/upload errors, not for "link revoked" etc.
  if (STATE.message === T.errUploadFailed) {
    const retry = el('button', 'btn', T.retry);
    retry.type = 'button';
    retry.onclick = () => start();
    wrap.appendChild(retry);
  }
  return wrap;
}

// --- upload flow --------------------------------------------------------

async function startUpload() {
  const { config, token, files, schema, senderInfo } = STATE;
  const total = files.reduce((s, f) => s + f.size, 0);
  if (total > config.max_upload_size_bytes) {
    transition({ name: 'error', message: T.errTooLarge(toMB(config.max_upload_size_bytes)), config });
    return;
  }

  // R5: required-field + email-shape validation. Server cannot validate
  // (E2E) — this is the only gate. Stay in ready state and surface the
  // error inline rather than transitioning to a terminal error screen.
  if (senderInfo) {
    const validationError = validateSenderInfo(schema, senderInfo);
    if (validationError) {
      transition({ ...STATE, senderInfoError: validationError });
      return;
    }
  }

  // Never send a completely empty submission (the recipient would get a blank
  // arrival). This covers form-only links and files-optional links where the
  // sender attached nothing: require at least one file or one filled field.
  if (files.length === 0 && !hasAnyValue(senderInfo)) {
    transition({
      ...STATE,
      senderInfoError: isFormOnly(config) ? T.formOnlyEmpty : T.filesOptionalEmpty,
    });
    return;
  }

  transition({ name: 'encrypting', config, total });
  // Yield once so the spinner paints before we hog the main thread.
  await new Promise((r) => requestAnimationFrame(r));

  let prekey, sharedSecret, kemCiphertext, iv, encrypted;
  try {
    prekey = await getPrekey(config.handle);
  } catch (err) {
    transition({ name: 'error', message: errorMessage(err), config });
    return;
  }

  try {
    verifyPrekey({
      kem_pk: fromBase64(prekey.kem_pk),
      signature: fromBase64(prekey.signature),
      id_pk: fromBase64(prekey.id_pk),
    });
  } catch {
    transition({ name: 'error', message: T.errIntegrity, config });
    return;
  }

  try {
    const cap = encapsulate(fromBase64(prekey.kem_pk));
    kemCiphertext = cap.ciphertext;
    sharedSecret = cap.sharedSecret;

    // Bundle the form-filled metadata alongside the user's files. When
    // senderInfo is null (legacy link, no schema, or schema with no
    // enabled fields) buildZip skips the synthetic file entirely.
    const bundlePayload = senderInfo ? buildSenderInfoPayload(senderInfo) : null;
    const zip = await buildZip(files, bundlePayload);
    const envelope = wrapWithVersion(zip);

    // Yield again so the "Encrypting…" label remains responsive.
    await new Promise((r) => requestAnimationFrame(r));

    const enc = await encryptAesGcm(sharedSecret, envelope);
    iv = enc.iv;
    encrypted = enc.encrypted;
  } catch (err) {
    console.error(err);
    transition({ name: 'error', message: T.errUploadFailed, config });
    return;
  }

  transition({ name: 'uploading', config, pct: 0 });

  try {
    const result = await uploadBlob(config.handle, token, {
      prekeyId: prekey.prekey_id,
      kemCiphertextB64: toBase64(kemCiphertext),
      ivB64: toBase64(iv),
      data: encrypted,
    }, (loaded, total) => {
      const pct = Math.min(100, Math.floor((loaded / total) * 100));
      if (STATE.name === 'uploading' && STATE.pct !== pct) {
        transition({ name: 'uploading', config, pct });
      }
    });
    transition({ name: 'done', config, blob_id: result.blob_id });
  } catch (err) {
    transition({ name: 'error', message: errorMessage(err), config });
  }
}

// --- R5 sender-info helpers --------------------------------------------

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

/** True when the sender filled at least one form value (any fixed or custom). */
function hasAnyValue(senderInfo) {
  if (!senderInfo) return false;
  if (['name', 'surname', 'email', 'phone'].some((k) => String(senderInfo[k] || '').trim() !== '')) {
    return true;
  }
  const custom = Array.isArray(senderInfo.custom) ? senderInfo.custom : [];
  return custom.some((c) => String((c && c.value) || '').trim() !== '');
}

/**
 * Return the error message to display when required fields are blank
 * or the email shape is wrong, otherwise null. Required-flag is taken
 * from the schema; both fixed fields and custom fields are checked.
 */
function validateSenderInfo(schema, senderInfo) {
  if (!schema) return null;
  const f = schema.fixed || {};
  const missingRequired = (key) =>
    f[key] && f[key].enabled && f[key].required &&
    String(senderInfo[key] || '').trim() === '';
  if (missingRequired('name') ||
      missingRequired('surname') ||
      missingRequired('email') ||
      missingRequired('phone')) {
    return T.senderInfoMissingRequired;
  }

  // Email shape — only check when the field is enabled and non-empty.
  // A blank optional email is fine.
  if (f.email && f.email.enabled) {
    const v = String(senderInfo.email || '').trim();
    if (v && !EMAIL_PATTERN.test(v)) return T.senderInfoInvalidEmail;
  }

  const custom = Array.isArray(schema.custom) ? schema.custom : [];
  for (let i = 0; i < custom.length; i++) {
    if (custom[i].required) {
      const v = (senderInfo.custom[i] && senderInfo.custom[i].value) || '';
      if (String(v).trim() === '') return T.senderInfoMissingRequired;
    }
  }

  return null;
}

/**
 * Build the JSON payload that lands inside the encrypted bundle as
 * _grataq_sender_info.json. The 4 fixed fields are always present
 * (empty string when disabled or blank) so the receiver can read
 * by stable key without optional-chaining; custom fields are an
 * ordered list of {label, value} mirroring the schema order.
 */
function buildSenderInfoPayload(senderInfo) {
  return {
    version: 1,
    name: String(senderInfo.name || ''),
    surname: String(senderInfo.surname || ''),
    email: String(senderInfo.email || ''),
    phone: String(senderInfo.phone || ''),
    custom: (senderInfo.custom || []).map((c) => ({
      label: String(c.label || ''),
      value: String(c.value || ''),
    })),
  };
}

// --- error mapping ------------------------------------------------------

function errorMessage(err) {
  if (err instanceof LinkNotFoundError) return T.errLinkNotFound;
  if (err instanceof LinkExpiredError) return T.errLinkExpired;
  if (err instanceof LinkPausedError) return T.errPaused;
  if (err instanceof NoPrekeysError) return T.errNoPrekeys;
  if (err instanceof TooLargeError) return T.errTooLarge(STATE.config ? toMB(STATE.config.max_upload_size_bytes) : '?');
  if (err instanceof IntegrityError) return T.errIntegrity;
  if (err instanceof UploadFailedError) return T.errUploadFailed;
  console.error(err);
  return T.errUploadFailed;
}

// --- DOM helpers --------------------------------------------------------

function el(tag, cls, text) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

function makeFileInput({ accept, multiple, capture }) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.multiple = !!multiple;
  if (capture) input.setAttribute('capture', capture);
  input.className = 'visually-hidden';
  input.onchange = (ev) => {
    const picked = Array.from(ev.target.files || []);
    if (picked.length === 0) return;
    const next = (STATE.files || []).concat(picked);
    transition({ ...STATE, files: next });
  };
  return input;
}

function absoluteApi(url) {
  if (/^https?:\/\//i.test(url)) return url;
  const base = apiBase();
  return base.replace(/\/$/, '') + (url.startsWith('/') ? url : '/' + url);
}

/**
 * Ensure a tenant-supplied URL is absolute. The recipient's public_url is
 * stored verbatim and may arrive without a scheme ("www.banka.cz/docs");
 * rendered as-is into an <a href> the browser would resolve it relative to
 * send.grataq.com. Prepend https:// when no http(s) scheme is present.
 */
function externalUrl(url) {
  const u = String(url || '').trim();
  if (!u) return u;
  return /^https?:\/\//i.test(u) ? u : 'https://' + u;
}

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function toMB(bytes) {
  return Math.round(bytes / (1024 * 1024));
}
